import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, Users, Calendar, Play, Save, History, Settings, Edit, Trash2, Link as LinkIcon, Pause, PlayCircle, Archive, Plus, AlertTriangle, CheckCircle, LogOut, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "./AuthContext";
import Login from "./Login";
import AdminPanel from "./AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

const COURT_OPTIONS = [
  { label: "Padel 1", value: "Padel 1", id: 2615 },
  { label: "Padel 2", value: "Padel 2", id: 2616 }
];

const WEEKDAYS = [
  { value: 0, label: "Maandag" },
  { value: 1, label: "Dinsdag" },
  { value: 2, label: "Woensdag" },
  { value: 3, label: "Donderdag" },
  { value: 4, label: "Vrijdag" },
  { value: 5, label: "Zaterdag" },
  { value: 6, label: "Zondag" }
];

const Dashboard = () => {
  const [config, setConfig] = useState({
    base_url: "https://oudenrijn.baanreserveren.nl",
    bookers: [],
    auto_booking_enabled: false,
    scheduler_time: "00:00",
    weeks_ahead: 2
  });
  const [configLoaded, setConfigLoaded] = useState(false);

  const [slots, setSlots] = useState([]);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Booker form state
  const [bookerForm, setBookerForm] = useState({
    username: "",
    password: "",
    full_name: ""
  });
  const [editingBookerIndex, setEditingBookerIndex] = useState(null);
  const [isBookerDialogOpen, setIsBookerDialogOpen] = useState(false);
  
  // Form state for adding/editing slots
  const [slotForm, setSlotForm] = useState({
    weekday: 2,
    start_time: "15:00",
    end_time: "16:00",
    court: "Padel 2",
    court_id: 2616,
    booker_username: "",
    player2_name: "Dumile Makatini",
    player3_name: "Tom Straeter",
    player4_name: "Frank Vernooij",
    repeat_weekly: false
  });
  
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [selectedSlotForExec, setSelectedSlotForExec] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedBookingDate, setSelectedBookingDate] = useState("");

  // Helper function to calculate end time (start + 1 hour)
  const calculateEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':');
    const endHour = (parseInt(hours) + 1).toString().padStart(2, '0');
    return `${endHour}:${minutes}`;
  };

  // Check subscription status
  const getSubscriptionStatus = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: "expired", label: "Verlopen", variant: "destructive" };
    } else if (diffDays <= 14) {
      return { status: "expiring", label: "Verloopt binnenkort", variant: "warning" };
    }
    return { status: "active", label: "Actief", variant: "success" };
  };

  useEffect(() => {
    loadConfiguration();
    loadSlots();
    loadHistory();
    loadStatus();
  }, []);

  const loadConfiguration = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      if (response.data.status === "success" && response.data.config) {
        setConfig(response.data.config);
      }
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setConfigLoaded(true);
    }
  };

  const loadSlots = async () => {
    try {
      const response = await axios.get(`${API}/slots`);
      if (response.data.status === "success") {
        setSlots(response.data.slots);
      }
    } catch (error) {
      console.error("Error loading slots:", error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/bookings/history`);
      if (response.data.status === "success") {
        setHistory(response.data.logs);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/bookings/status`);
      if (response.data.status === "success") {
        setStatus(response.data);
      }
    } catch (error) {
      console.error("Error loading status:", error);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/config`, config);
      toast.success("Configuratie opgeslagen!");
      await loadStatus();
    } catch (error) {
      toast.error("Fout bij opslaan configuratie");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBooker = async () => {
    if (!bookerForm.username || !bookerForm.password || !bookerForm.full_name) {
      toast.error("Vul alle velden in");
      return;
    }

    setLoading(true);
    try {
      // Validate booker and get subscription info
      const validateResponse = await axios.post(`${API}/bookers/validate`, bookerForm, {
        params: { base_url: config.base_url }
      });

      const subscriptionDate = validateResponse.data.subscription_expiry_date;
      
      const newBooker = {
        ...bookerForm,
        subscription_expiry_date: subscriptionDate
      };

      let updatedConfig;
      if (editingBookerIndex !== null) {
        // Update existing booker
        const updatedBookers = [...config.bookers];
        updatedBookers[editingBookerIndex] = newBooker;
        updatedConfig = { ...config, bookers: updatedBookers };
      } else {
        // Add new booker
        updatedConfig = { ...config, bookers: [...config.bookers, newBooker] };
      }

      // Save to backend immediately
      await axios.post(`${API}/config`, updatedConfig);
      
      setConfig(updatedConfig);
      toast.success(editingBookerIndex !== null ? "Hoofdboeker bijgewerkt en opgeslagen!" : "Hoofdboeker toegevoegd en opgeslagen!");

      resetBookerForm();
      setIsBookerDialogOpen(false);
      await loadStatus();
    } catch (error) {
      toast.error("Fout bij valideren hoofdboeker");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBooker = (booker, index) => {
    setBookerForm({
      username: booker.username,
      password: booker.password,
      full_name: booker.full_name
    });
    setEditingBookerIndex(index);
    setIsBookerDialogOpen(true);
  };

  const handleDeleteBooker = async (index) => {
    if (!window.confirm("Weet u zeker dat u deze hoofdboeker wilt verwijderen?")) {
      return;
    }
    
    setLoading(true);
    try {
      const updatedBookers = config.bookers.filter((_, i) => i !== index);
      const updatedConfig = { ...config, bookers: updatedBookers };
      
      // Save to backend immediately
      await axios.post(`${API}/config`, updatedConfig);
      
      setConfig(updatedConfig);
      toast.success("Hoofdboeker verwijderd en opgeslagen!");
      await loadStatus();
    } catch (error) {
      toast.error("Fout bij verwijderen hoofdboeker");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetBookerForm = () => {
    setBookerForm({
      username: "",
      password: "",
      full_name: ""
    });
    setEditingBookerIndex(null);
  };

  const handleAddSlot = async () => {
    if (!slotForm.booker_username) {
      toast.error("Selecteer een hoofdboeker");
      return;
    }

    setLoading(true);
    try {
      if (editingSlotId) {
        // Update existing slot
        await axios.put(`${API}/slots/${editingSlotId}`, slotForm);
        toast.success("Tijdslot bijgewerkt!");
      } else {
        // Create new slot
        await axios.post(`${API}/slots`, slotForm);
        toast.success("Tijdslot toegevoegd!");
      }
      
      await loadSlots();
      resetSlotForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error("Fout bij opslaan tijdslot");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlot = (slot) => {
    setSlotForm({
      weekday: slot.weekday,
      start_time: slot.start_time,
      end_time: slot.end_time,
      court: slot.court,
      court_id: slot.court_id,
      booker_username: slot.booker_username,
      player2_name: slot.player2_name,
      player3_name: slot.player3_name,
      player4_name: slot.player4_name,
      repeat_weekly: slot.repeat_weekly
    });
    setEditingSlotId(slot.slot_id);
    setIsDialogOpen(true);
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm("Weet u zeker dat u dit tijdslot wilt verwijderen?")) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/slots/${slotId}`);
      toast.success("Tijdslot verwijderd!");
      await loadSlots();
    } catch (error) {
      toast.error("Fout bij verwijderen tijdslot");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (slotId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    
    try {
      await axios.put(`${API}/slots/${slotId}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Tijdslot geactiveerd!" : "Tijdslot gepauzeerd!");
      await loadSlots();
    } catch (error) {
      toast.error("Fout bij wijzigen status");
      console.error(error);
    }
  };

  const handleArchiveSlot = async (slotId) => {
    try {
      await axios.put(`${API}/slots/${slotId}`, { status: "archived" });
      toast.success("Tijdslot gearchiveerd!");
      await loadSlots();
    } catch (error) {
      toast.error("Fout bij archiveren");
      console.error(error);
    }
  };

  const handleOpenExecuteDialog = async (slot) => {
    setSelectedSlotForExec(slot);
    
    // Get available dates for this slot
    try {
      const response = await axios.get(`${API}/slots/${slot.slot_id}/available-dates`);
      if (response.data.status === "success") {
        setAvailableDates(response.data.dates);
        if (response.data.dates.length > 0) {
          setSelectedBookingDate(response.data.dates[0].date);
        }
      }
    } catch (error) {
      console.error("Error loading dates:", error);
    }
    
    setExecuteDialogOpen(true);
  };

  const handleExecuteSlot = async () => {
    if (!selectedSlotForExec || !selectedBookingDate) {
      return;
    }
    
    setLoading(true);
    setExecuteDialogOpen(false);
    toast.info("Boeking wordt uitgevoerd...");
    
    try {
      const response = await axios.post(
        `${API}/slots/${selectedSlotForExec.slot_id}/execute?booking_date=${selectedBookingDate}`
      );
      
      if (response.data.result.status === "success") {
        toast.success("Boeking succesvol!");
      } else {
        toast.error("Boeking mislukt: " + response.data.result.message);
      }
      
      await loadHistory();
    } catch (error) {
      // Show detailed error message
      const errorMsg = error.response?.data?.detail || error.message || "Onbekende fout";
      toast.error("Fout bij uitvoeren boeking: " + errorMsg);
      console.error(error);
    } finally {
      setLoading(false);
      setSelectedSlotForExec(null);
      setSelectedBookingDate("");
    }
  };

  const handleDebugTest = async (slot) => {
    setLoading(true);
    toast.info("🔍 Debug test wordt uitgevoerd... Dit kan 30 seconden duren.");
    
    try {
      // Get available dates for this slot
      const datesResponse = await axios.get(`${API}/slots/${slot.slot_id}/available-dates`);
      let testDate = "";
      if (datesResponse.data.status === "success" && datesResponse.data.dates.length > 0) {
        testDate = datesResponse.data.dates[0].date;
      } else {
        toast.error("Geen beschikbare datums gevonden voor dit slot");
        setLoading(false);
        return;
      }
      
      // Run debug test
      const response = await axios.post(
        `${API}/debug/test-deeplink/${slot.slot_id}?booking_date=${testDate}`
      );
      
      const result = response.data;
      
      // Show results in a detailed toast or alert
      if (result.status === "debug") {
        const analysis = result.page_analysis || {};
        const verderFound = analysis.verder_text_found ? "✅ JA" : "❌ NEE";
        const bevestigenFound = analysis.bevestigen_text_found ? "✅ JA" : "❌ NEE";
        const selectCount = analysis.select_count || 0;
        const clickableCount = (analysis.clickable_elements || []).length;
        const bookingSiteErrors = analysis.booking_site_errors || [];
        const smartVerderTest = analysis.smart_verder_test || {};
        const inputValues = analysis.input_button_values || [];
        
        // Log detailed results to console
        console.log("🔍 DEBUG RESULT:", result);
        console.log("📋 Clickable elements:", analysis.clickable_elements);
        console.log("📝 Page text:", analysis.page_text_preview);
        console.log("🔘 Input button values:", inputValues);
        console.log("🧠 Smart Verder test:", smartVerderTest);
        console.log("❌ Booking site errors:", bookingSiteErrors);
        
        // Check for booking site errors
        if (bookingSiteErrors.length > 0) {
          toast.error(
            `⚠️ BOOKING SITE FOUT:\n${bookingSiteErrors.join('\n')}\n\n` +
            `Dit betekent dat deze datum niet boekbaar is op de booking site.`,
            { duration: 15000 }
          );
        }
        
        // Show smart test result
        const smartResult = smartVerderTest.success 
          ? `✅ SMART KLIK GELUKT: ${smartVerderTest.message}` 
          : `❌ SMART KLIK MISLUKT: ${smartVerderTest.message || 'geen info'}`;
        
        // Show summary
        toast.success(
          `Debug complete!\n` +
          `${smartResult}\n` +
          `Input buttons: ${inputValues.join(', ') || 'geen'}\n` +
          `Screenshot: ${analysis.screenshot_note || ''}\n` +
          `Check console (F12) voor details.`,
          { duration: 20000 }
        );
        
        // If screenshot available, show it
        if (result.screenshot_base64) {
          // Open in new tab
          const img = `data:image/png;base64,${result.screenshot_base64}`;
          const newWindow = window.open();
          newWindow.document.write(`<img src="${img}" style="max-width: 100%;" />`);
          newWindow.document.title = "Debug Screenshot";
        }
      } else {
        toast.error(`Debug test failed: ${result.error || "Unknown error"}`);
      }
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Onbekende fout";
      toast.error("Debug test fout: " + errorMsg);
      console.error("Debug test error:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetSlotForm = () => {
    setSlotForm({
      weekday: 2,
      start_time: "15:00",
      end_time: "16:00",
      court: "Padel 2",
      court_id: 2616,
      booker_username: config.bookers.length > 0 ? config.bookers[0].username : "",
      player2_name: "Dumile Makatini",
      player3_name: "Tom Straeter",
      player4_name: "Frank Vernooij",
      repeat_weekly: false
    });
    setEditingSlotId(null);
  };

  const handleStartTimeChange = (newStartTime) => {
    setSlotForm({
      ...slotForm,
      start_time: newStartTime,
      end_time: calculateEndTime(newStartTime)
    });
  };

  const handleCourtChange = (courtValue) => {
    const court = COURT_OPTIONS.find(c => c.value === courtValue);
    setSlotForm({
      ...slotForm,
      court: court.value,
      court_id: court.id
    });
  };

  const activeSlots = slots.filter(s => s.status === "active" || s.status === "paused");
  const archivedSlots = slots.filter(s => s.status === "archived");

  const getStatusBadge = (status) => {
    if (status === "active") {
      return <Badge className="bg-green-500">Actief</Badge>;
    } else if (status === "paused") {
      return <Badge className="bg-orange-500">Gepauzeerd</Badge>;
    } else {
      return <Badge variant="secondary">Gearchiveerd</Badge>;
    }
  };

  const getBookerDisplayName = (username) => {
    const booker = config.bookers.find(b => b.username === username);
    if (!booker) return username;
    
    const subscriptionStatus = getSubscriptionStatus(booker.subscription_expiry_date);
    return (
      <span className="flex items-center gap-2">
        {booker.full_name} ({username})
        {subscriptionStatus && subscriptionStatus.status !== "active" && (
          <Badge variant={subscriptionStatus.variant} className="text-xs">
            {subscriptionStatus.label}
          </Badge>
        )}
      </span>
    );
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-container">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-[#5EECC7]" data-testid="page-title">Padel Baan Preserve</h1>
            <p className="text-sm sm:text-base text-gray-400" data-testid="page-subtitle">
              Automatisch boekingssysteem voor padelbanen
            </p>
          </div>
        </div>

        <Tabs defaultValue="config" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-[#1a1a1a] border border-[#5EECC7]/20" data-testid="tabs-list">
            <TabsTrigger value="config" data-testid="tab-config" className="text-xs sm:text-sm data-[state=active]:bg-[#5EECC7] data-[state=active]:text-gray-900 text-gray-300">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Configuratie</span>
              <span className="sm:hidden">Config</span>
            </TabsTrigger>
            <TabsTrigger value="slots" data-testid="tab-slots" className="text-xs sm:text-sm data-[state=active]:bg-[#5EECC7] data-[state=active]:text-gray-900 text-gray-300">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Tijdslots
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="text-xs sm:text-sm data-[state=active]:bg-[#5EECC7] data-[state=active]:text-gray-900 text-gray-300">
              <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Geschiedenis</span>
              <span className="sm:hidden">Historie</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="space-y-4 sm:space-y-6">
            <Card data-testid="url-card">
              <CardHeader>
                <CardTitle>
                  <LinkIcon className="inline w-5 h-5 mr-2" />
                  Booking Website URL
                </CardTitle>
                <CardDescription>URL van het boekingssysteem (voor andere clubs)</CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="base_url">Base URL</Label>
                  <Input
                    id="base_url"
                    data-testid="input-base-url"
                    value={config.base_url}
                    onChange={(e) => setConfig({ ...config, base_url: e.target.value })}
                    placeholder="https://oudenrijn.baanreserveren.nl"
                  />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="bookers-card">
              <CardHeader>
                <CardTitle>
                  <Users className="inline w-5 h-5 mr-2" />
                  Hoofdboeker Accounts {configLoaded ? `(${config.bookers.length})` : "(laden...)"}
                </CardTitle>
                <CardDescription>
                  Beheer de accounts waarmee geboekt wordt. Abonnement status wordt gecontroleerd.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!configLoaded ? (
                  <p className="text-muted-foreground text-center py-4">Configuratie laden...</p>
                ) : config.bookers.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">Nog geen hoofdboekers toegevoegd</p>
                ) : (
                  <div className="space-y-3">
                    {config.bookers.map((booker, index) => {
                      const subscriptionStatus = getSubscriptionStatus(booker.subscription_expiry_date);
                      return (
                        <div key={index} className="p-3 sm:p-4 border rounded-lg">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-sm sm:text-base">{booker.full_name}</span>
                                {subscriptionStatus && (
                                  <Badge 
                                    variant={subscriptionStatus.variant}
                                    className={`text-xs ${subscriptionStatus.status === "expired" ? "bg-red-500" : subscriptionStatus.status === "expiring" ? "bg-yellow-500" : "bg-green-500"}`}
                                  >
                                    {subscriptionStatus.status === "expired" && <AlertTriangle className="w-3 h-3 mr-1" />}
                                    {subscriptionStatus.status === "active" && <CheckCircle className="w-3 h-3 mr-1" />}
                                    {subscriptionStatus.label}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                                Gebruikersnaam: {booker.username}
                              </div>
                              {booker.subscription_expiry_date && (
                                <div className="text-xs sm:text-sm text-muted-foreground">
                                  Abonnement geldig tot: {new Date(booker.subscription_expiry_date).toLocaleDateString('nl-NL')}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditBooker(booker, index)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteBooker(index)}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Dialog open={isBookerDialogOpen} onOpenChange={(open) => {
                  setIsBookerDialogOpen(open);
                  if (!open) resetBookerForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="w-full text-sm sm:text-base" variant="outline">
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      Meer hoofdboekers toevoegen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingBookerIndex !== null ? "Hoofdboeker Bewerken" : "Nieuwe Hoofdboeker"}</DialogTitle>
                      <DialogDescription>
                        Vul de inloggegevens in. Abonnement status wordt automatisch opgehaald.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Gebruikersnaam</Label>
                        <Input
                          value={bookerForm.username}
                          onChange={(e) => setBookerForm({ ...bookerForm, username: e.target.value })}
                          placeholder="Basvl"
                        />
                      </div>
                      
                      <div>
                        <Label>Wachtwoord</Label>
                        <Input
                          type="password"
                          value={bookerForm.password}
                          onChange={(e) => setBookerForm({ ...bookerForm, password: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label>Volledige Spelernaam</Label>
                        <Input
                          value={bookerForm.full_name}
                          onChange={(e) => setBookerForm({ ...bookerForm, full_name: e.target.value })}
                          placeholder="B V Liere"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddBooker}
                          disabled={loading}
                          className="flex-1"
                        >
                          {loading ? "Valideren..." : (editingBookerIndex !== null ? "Bijwerken" : "Toevoegen")}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsBookerDialogOpen(false);
                            resetBookerForm();
                          }}
                        >
                          Annuleren
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            <Card data-testid="automation-card">
              <CardHeader>
                <CardTitle>
                  <Clock className="inline w-5 h-5 mr-2" />
                  Automatisering
                </CardTitle>
                <CardDescription>Configureer automatische boekingen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Automatisch boeken inschakelen</Label>
                    <p className="text-sm text-muted-foreground">Schakel automatische boekingen in/uit</p>
                  </div>
                  <Switch
                    data-testid="switch-auto-booking"
                    checked={config.auto_booking_enabled}
                    onCheckedChange={(checked) => setConfig({ ...config, auto_booking_enabled: checked })}
                  />
                </div>
                
                {config.auto_booking_enabled && (
                  <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                    <div className="space-y-2">
                      <Label htmlFor="scheduler-time">Dagelijkse uitvoertijd (24-uurs notatie)</Label>
                      <Input
                        id="scheduler-time"
                        type="time"
                        value={config.scheduler_time}
                        onChange={(e) => setConfig({ ...config, scheduler_time: e.target.value })}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-muted-foreground">Tijd waarop dagelijks geboekt wordt (Amsterdam timezone)</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="weeks-ahead">Aantal weken vooruit boeken</Label>
                      <select
                        id="weeks-ahead"
                        value={config.weeks_ahead}
                        onChange={(e) => setConfig({ ...config, weeks_ahead: parseInt(e.target.value) })}
                        className="w-full max-w-xs px-3 py-2 border border-input bg-background rounded-md"
                      >
                        <option value="1">1 week vooruit (voor testen)</option>
                        <option value="2">2 weken vooruit (standaard)</option>
                        <option value="3">3 weken vooruit</option>
                        <option value="4">4 weken vooruit</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Hoeveel weken vooruit moet er geboekt worden (clubspecifiek)
                      </p>
                    </div>
                  </div>
                )}
                
                {status && (
                  <div className="mt-4 p-4 bg-muted rounded-lg" data-testid="status-display">
                    <h3 className="font-semibold mb-2">Scheduler Status</h3>
                    <p className="text-sm">
                      Status: <span className="font-medium">{status.scheduler_running ? "Actief" : "Inactief"}</span>
                    </p>
                    {status.scheduled_jobs && status.scheduled_jobs.length > 0 && (
                      <p className="text-sm mt-2">
                        Volgende run: {new Date(status.scheduled_jobs[0].next_run).toLocaleString("nl-NL", { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: false 
                        })}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={handleSaveConfig}
              disabled={loading}
              className="w-full bg-[#5EECC7] hover:bg-[#4dd9b4] text-gray-900 font-semibold"
              size="lg"
              data-testid="btn-save-config"
            >
              <Save className="w-5 h-5 mr-2" />
              Configuratie Opslaan
            </Button>
          </TabsContent>

          <TabsContent value="slots" className="space-y-6">
            <Card data-testid="add-slot-card">
              <CardHeader>
                <CardTitle>Tijdslot Toevoegen</CardTitle>
                <CardDescription>Configureer een nieuw wekelijks tijdslot</CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={isDialogOpen} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) resetSlotForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="w-full text-xs sm:text-sm" data-testid="btn-open-slot-dialog" disabled={config.bookers.length === 0}>
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      {config.bookers.length === 0 ? "Voeg eerst een hoofdboeker toe" : "Nieuw Tijdslot Toevoegen"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingSlotId ? "Tijdslot Bewerken" : "Nieuw Tijdslot"}</DialogTitle>
                      <DialogDescription>
                        Vul de details in voor de wekelijkse boeking
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Hoofdboeker</Label>
                        <select
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                          value={slotForm.booker_username}
                          onChange={(e) => setSlotForm({ ...slotForm, booker_username: e.target.value })}
                        >
                          <option value="">Selecteer hoofdboeker</option>
                          {config.bookers.map((booker) => {
                            const subscriptionStatus = getSubscriptionStatus(booker.subscription_expiry_date);
                            return (
                              <option key={booker.username} value={booker.username}>
                                {booker.full_name} ({booker.username})
                                {subscriptionStatus && subscriptionStatus.status !== "active" ? ` - ${subscriptionStatus.label}` : ""}
                              </option>
                            );
                          })}
                        </select>
                        {slotForm.booker_username && (() => {
                          const selectedBooker = config.bookers.find(b => b.username === slotForm.booker_username);
                          const subscriptionStatus = selectedBooker ? getSubscriptionStatus(selectedBooker.subscription_expiry_date) : null;
                          return subscriptionStatus && subscriptionStatus.status !== "active" && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                              <AlertTriangle className="w-4 h-4 inline mr-2" />
                              Waarschuwing: Abonnement {subscriptionStatus.label.toLowerCase()}
                            </div>
                          );
                        })()}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Weekdag</Label>
                          <select
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                            data-testid="select-weekday"
                            value={slotForm.weekday}
                            onChange={(e) => setSlotForm({ ...slotForm, weekday: parseInt(e.target.value) })}
                          >
                            {WEEKDAYS.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label>Baan</Label>
                          <select
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                            data-testid="select-court"
                            value={slotForm.court}
                            onChange={(e) => handleCourtChange(e.target.value)}
                          >
                            {COURT_OPTIONS.map((court) => (
                              <option key={court.value} value={court.value}>
                                {court.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Begintijd</Label>
                          <Input
                            type="time"
                            data-testid="input-start-time"
                            value={slotForm.start_time}
                            onChange={(e) => handleStartTimeChange(e.target.value)}
                          />
                        </div>
                        
                        <div>
                          <Label>Eindtijd (automatisch +1 uur)</Label>
                          <Input
                            type="time"
                            data-testid="input-end-time"
                            value={slotForm.end_time}
                            disabled
                            className="bg-muted"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label>Spelers</Label>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-sm text-muted-foreground">
                              Speler 1 (hoofdboeker): {config.bookers.find(b => b.username === slotForm.booker_username)?.full_name || "Selecteer eerst hoofdboeker"}
                            </Label>
                          </div>
                          
                          <div>
                            <Label className="text-sm">Speler 2</Label>
                            <Input
                              data-testid="input-player2"
                              value={slotForm.player2_name}
                              onChange={(e) => setSlotForm({ ...slotForm, player2_name: e.target.value })}
                              placeholder="Dumile Makatini"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm">Speler 3</Label>
                            <Input
                              data-testid="input-player3"
                              value={slotForm.player3_name}
                              onChange={(e) => setSlotForm({ ...slotForm, player3_name: e.target.value })}
                              placeholder="Tom Straeter"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm">Speler 4</Label>
                            <Input
                              data-testid="input-player4"
                              value={slotForm.player4_name}
                              onChange={(e) => setSlotForm({ ...slotForm, player4_name: e.target.value })}
                              placeholder="Frank Vernooij"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="repeat_weekly"
                          data-testid="checkbox-repeat-weekly"
                          checked={slotForm.repeat_weekly}
                          onCheckedChange={(checked) => setSlotForm({ ...slotForm, repeat_weekly: checked })}
                        />
                        <Label htmlFor="repeat_weekly" className="cursor-pointer">
                          Iedere opvolgende week herhalen
                        </Label>
                      </div>
                      
                      <div className="flex gap-2 mt-6">
                        <Button
                          onClick={handleAddSlot}
                          disabled={loading}
                          className="flex-1"
                          data-testid="btn-save-slot"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {editingSlotId ? "Bijwerken" : "Opslaan"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsDialogOpen(false);
                            resetSlotForm();
                          }}
                          data-testid="btn-cancel-slot"
                        >
                          Annuleren
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            <Card data-testid="slots-list-card">
              <CardHeader>
                <CardTitle>Actieve & Gepauzeerde Tijdslots</CardTitle>
                <CardDescription>Overzicht van alle geplande boekingen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activeSlots.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8" data-testid="no-slots">
                      Nog geen tijdslots geconfigureerd
                    </p>
                  ) : (
                    activeSlots.map((slot, index) => (
                      <div
                        key={slot.slot_id}
                        className="p-3 sm:p-4 border rounded-lg space-y-3"
                        data-testid={`slot-item-${index}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <div className="font-semibold text-base sm:text-lg">
                                {WEEKDAYS.find(d => d.value === slot.weekday)?.label}
                              </div>
                              {getStatusBadge(slot.status)}
                              {slot.repeat_weekly && (
                                <Badge variant="outline" className="text-blue-600 text-xs">
                                  🔄 Wekelijks
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                              <span className="font-medium">{slot.court}</span> • 
                              {slot.start_time} - {slot.end_time}
                            </div>
                            <div className="text-xs sm:text-sm mt-2">
                              <span className="font-medium">Geboekt door:</span> {getBookerDisplayName(slot.booker_username)}
                            </div>
                            <div className="text-xs sm:text-sm mt-1 break-words">
                              <span className="font-medium">Spelers:</span> {config.bookers.find(b => b.username === slot.booker_username)?.full_name}, {slot.player2_name}, {slot.player3_name}, {slot.player4_name}
                            </div>
                            <div className="text-xs sm:text-sm mt-2 p-2 bg-muted rounded">
                              <div className="break-words"><span className="font-medium">Volgende trigger:</span> {slot.next_trigger_date || "Berekenen..."}</div>
                              <div className="break-words"><span className="font-medium">Boekt voor:</span> {slot.next_booking_date || "Berekenen..."}</div>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap sm:flex-col gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSlot(slot)}
                              data-testid={`btn-edit-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Bewerken</span>
                              <span className="sm:hidden">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant={slot.status === "active" ? "secondary" : "default"}
                              onClick={() => handleToggleStatus(slot.slot_id, slot.status)}
                              data-testid={`btn-toggle-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              {slot.status === "active" ? (
                                <><Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Pauzeren</span><span className="sm:hidden">Pauze</span></>
                              ) : (
                                <><PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /><span className="hidden sm:inline">Activeren</span><span className="sm:hidden">Start</span></>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleOpenExecuteDialog(slot)}
                              disabled={loading}
                              data-testid={`btn-execute-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Uitvoeren</span>
                              <span className="sm:hidden">Nu</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDebugTest(slot)}
                              disabled={loading}
                              data-testid={`btn-debug-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Debug</span>
                              <span className="sm:hidden">🔍</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleArchiveSlot(slot.slot_id)}
                              data-testid={`btn-archive-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Archive className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Archiveer</span>
                              <span className="sm:hidden">Arch</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteSlot(slot.slot_id)}
                              data-testid={`btn-delete-slot-${index}`}
                              className="text-xs sm:text-sm h-8 sm:h-9"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Verwijder</span>
                              <span className="sm:hidden">Del</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {archivedSlots.length > 0 && (
              <Card data-testid="archived-slots-card">
                <CardHeader>
                  <CardTitle>
                    <Archive className="inline w-5 h-5 mr-2" />
                    Gearchiveerde Tijdslots
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {archivedSlots.map((slot, index) => (
                      <div
                        key={slot.slot_id}
                        className="p-4 border rounded-lg opacity-60"
                        data-testid={`archived-slot-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">
                              {WEEKDAYS.find(d => d.value === slot.weekday)?.label} - {slot.court} - {slot.start_time}-{slot.end_time}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {slot.player2_name}, {slot.player3_name}, {slot.player4_name}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleStatus(slot.slot_id, "archived")}
                          >
                            <PlayCircle className="w-4 h-4 mr-1" />
                            Heractiveren
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Execute Confirmation Dialog */}
            <Dialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Boeking Uitvoeren</DialogTitle>
                  <DialogDescription>
                    Kies de datum waarvoor je wilt boeken
                  </DialogDescription>
                </DialogHeader>
                
                {selectedSlotForExec && (
                  <div className="space-y-4 mt-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="font-semibold">
                        {WEEKDAYS.find(d => d.value === selectedSlotForExec.weekday)?.label}
                      </div>
                      <div className="text-sm">
                        {selectedSlotForExec.court} • {selectedSlotForExec.start_time} - {selectedSlotForExec.end_time}
                      </div>
                      <div className="text-sm mt-1">
                        Geboekt door: {getBookerDisplayName(selectedSlotForExec.booker_username)}
                      </div>
                      <div className="text-sm mt-1">
                        Spelers: {config.bookers.find(b => b.username === selectedSlotForExec.booker_username)?.full_name}, {selectedSlotForExec.player2_name}, {selectedSlotForExec.player3_name}, {selectedSlotForExec.player4_name}
                      </div>
                    </div>

                    <div>
                      <Label>Kies Boekingsdatum</Label>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                        value={selectedBookingDate}
                        onChange={(e) => setSelectedBookingDate(e.target.value)}
                      >
                        {availableDates.map((date) => (
                          <option key={date.date} value={date.date}>
                            {date.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Alleen data binnen 2 weken worden getoond
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleExecuteSlot}
                        disabled={loading || !selectedBookingDate}
                        className="flex-1"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Uitvoeren
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setExecuteDialogOpen(false)}
                      >
                        Annuleren
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card data-testid="history-card">
              <CardHeader>
                <CardTitle>
                  <History className="inline w-5 h-5 mr-2" />
                  Boekings Geschiedenis
                </CardTitle>
                <CardDescription>Overzicht van alle uitgevoerde boekingen met screenshots</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {history.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8" data-testid="no-history">
                      Nog geen boekingen uitgevoerd
                    </p>
                  ) : (
                    history.map((log, index) => (
                      <div
                        key={log.log_id}
                        className="p-3 sm:p-4 border rounded-lg space-y-3"
                        data-testid={`history-item-${index}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm sm:text-base break-words">{log.message}</div>
                            <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                              {new Date(log.timestamp).toLocaleString("nl-NL", {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                              })}
                            </div>
                            {log.reservation_number && (
                              <div className="text-xs sm:text-sm mt-1">
                                <span className="font-medium">Reservering:</span> {log.reservation_number}
                              </div>
                            )}
                          </div>
                          <span
                            className={`px-2 sm:px-3 py-1 rounded text-xs font-semibold self-start sm:self-center ${
                              log.status === "success"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {log.status}
                          </span>
                        </div>
                        
                        {log.screenshot_base64 && (
                          <div className="mt-3">
                            <img
                              src={`data:image/png;base64,${log.screenshot_base64}`}
                              alt="Booking screenshot"
                              className="w-full max-w-full sm:max-w-md rounded border"
                            />
                          </div>
                        )}
                        
                        {log.error && (
                          <div className="text-sm text-red-600 mt-2">
                            <span className="font-medium">Fout:</span> {log.error}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

function App() {
  const { user, loading, login, logout, isImpersonating, exitImpersonation } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Laden...</div>
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={login} />;
  }

  return (
    <div className="App" style={{background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 50%, #1a2a2a 100%)', minHeight: '100vh'}}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-[#5EECC7] text-gray-900 px-3 sm:px-4 py-2 sm:py-3 text-center font-semibold flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm sm:text-base">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-xs sm:text-sm">Account van: {user.email}</span>
          </div>
          <Button
            size="sm"
            variant="destructive"
            onClick={async () => {
              try {
                await axios.post(`${API}/admin/impersonate/exit`, {}, { withCredentials: true });
                window.location.reload();
              } catch (error) {
                toast.error("Fout bij verlaten impersonate mode");
              }
            }}
            className="text-xs h-7"
          >
            Exit
          </Button>
        </div>
      )}

      {/* Navigation Bar */}
      <div className="bg-[#1a1a1a] border-b border-[#5EECC7]/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="/" className="text-base sm:text-xl font-bold text-[#5EECC7]">Padel Baan Preserve</a>
            {user.role === "admin" && (
              <a href="/admin" className="text-xs sm:text-sm text-gray-400 hover:text-[#5EECC7]">
                <Users className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                <span className="hidden sm:inline">Admin Panel</span>
                <span className="sm:hidden">Admin</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-xs sm:text-sm text-gray-400 hidden sm:inline">{user.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await logout();
                window.location.reload();
              }}
              className="h-8 text-xs sm:text-sm border-[#5EECC7]/50 text-[#5EECC7] hover:bg-[#5EECC7] hover:text-gray-900"
            >
              <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Uitloggen</span>
              <span className="sm:hidden">Uit</span>
            </Button>
          </div>
        </div>
      </div>

      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/admin" element={user.role === "admin" ? <AdminPanel /> : <Dashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
